<template>
  <section class="py-16 bg-neutral-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

      <!-- Section Header -->
      <div class="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
        <div>
          <p class="text-primary-500 text-sm font-semibold uppercase tracking-widest mb-2">Browse by Type</p>
          <h2 class="text-3xl md:text-4xl font-bold text-neutral-800">
            Discover Properties
          </h2>
        </div>
        <p class="text-neutral-500 max-w-sm text-sm leading-relaxed">
          Find your perfect property from our extensive collection across Rwanda
        </p>
      </div>

      <!-- Category Cards -->
      <div class="grid grid-cols-2 md:grid-cols-5 gap-5">
        <button
          v-for="category in categories"
          :key="category.typeFilter"
          @click="goToType(category)"
          class="group relative flex flex-col items-center gap-4 p-6 rounded-2xl border-2 border-neutral-200 bg-white text-neutral-700 hover:border-primary-300 hover:shadow-md transition-all duration-200 cursor-pointer text-left"
        >
          <!-- Icon container -->
          <div class="w-14 h-14 rounded-xl flex items-center justify-center bg-primary-50 group-hover:bg-primary-100 transition-colors">
            <svg
              class="w-7 h-7 text-primary-500"
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" :d="category.icon" />
            </svg>
          </div>

          <!-- Label + Count -->
          <div class="text-center">
            <p class="font-semibold text-base text-neutral-800">
              {{ category.label }}
            </p>
            <p class="text-xs mt-0.5 text-neutral-400">
              <template v-if="countsLoading">
                <span class="inline-block w-8 h-3 bg-neutral-200 rounded animate-pulse"></span>
              </template>
              <template v-else>
                {{ category.count.toLocaleString() }} Properties
              </template>
            </p>
          </div>
        </button>
      </div>

    </div>
  </section>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { propertiesApi } from '@/api/properties'

interface Category {
  label: string
  typeFilter: string
  count: number
  icon: string
}

@Options({})
export default class DiscoverProperties extends Vue {
  countsLoading = true

  categories: Category[] = [
    {
      label: 'Apartment',
      typeFilter: 'Apartment',
      count: 0,
      icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
    },
    {
      label: 'House/Villa',
      typeFilter: 'House/Villa',
      count: 0,
      icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6'
    },
    {
      label: 'Commercial',
      typeFilter: 'Commercial Space',
      count: 0,
      icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
    },
    {
      label: 'Plot',
      typeFilter: 'Plot',
      count: 0,
      icon: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7'
    },
    {
      label: 'Land',
      typeFilter: 'Land',
      count: 0,
      icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
    }
  ]

  async mounted () {
    await this.loadCounts()
  }

  async loadCounts () {
    this.countsLoading = true
    try {
      const results = await Promise.all(
        this.categories.map(c =>
          propertiesApi.list({ property_type: c.typeFilter, limit: 1 })
            .then(r => ({ typeFilter: c.typeFilter, total: r.data.total }))
            .catch(() => ({ typeFilter: c.typeFilter, total: 0 }))
        )
      )
      results.forEach(({ typeFilter, total }) => {
        const cat = this.categories.find(c => c.typeFilter === typeFilter)
        if (cat) cat.count = total
      })
    } finally {
      this.countsLoading = false
    }
  }

  goToType (category: Category) {
    this.$router.push({ name: 'properties', query: { type: category.typeFilter } })
  }
}
</script>
